import React, { useEffect, useId, useState } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import { ETIQUETA_MOTIVO, esMotivoHistorico } from '../types/metas.types';
import type { Aviso, FilaVista, MotivoSinMeta, OpcionMotivo, PendienteDeTienda, TiendaCatalogo } from '../types/metas.types';
import { metaOpcionalDeBodega, TEXTO_META_A_BODEGUERO } from '../utils/bodeguero';
import { DESCRIPCION_CASA, esFilaCasa, TITULO_META_CASA } from '../utils/casa';
import { ajusteDelGerente, conMarcasPorPersona, DIAS_INCAPACIDAD, marcasEnPalabras } from '../utils/captura';
import type { CambioFila } from '../utils/captura';
import { puedeCorregirCertificacion } from '../utils/catalogo';
import { distintivosDe } from '../utils/distintivos';
import type { SeveridadTag } from '../utils/estado';
import { pctConSigno, pesos, pesosEnteros } from '../utils/formato';
import { fechaCorta } from '../utils/semanas';
import { esPendiente } from '../utils/totales';
import { ApoyoDialog } from './ApoyoDialog';
import { HistorialColaboradorDialog } from './HistorialColaboradorDialog';
import { Avatar } from './ui/Avatar';
import { AccionMenu, MenuAcciones } from './ui/MenuAcciones';
import { CLASE_OVERLAY, CLASES_CAMPO, cx, EstadoCampo } from './ui/clases';

interface Props {
  filas: FilaVista[];
  tienda: { chain: string; num: number };
  /** Los motivos que se pueden ELEGIR (`mi-alcance.motivos`: sólo capturables). */
  motivos: OpcionMotivo[];
  /** Etiquetas de lo guardado antes del 29-sep (`mi-alcance.motivosHistoricos`). */
  motivosHistoricos?: OpcionMotivo[];
  soloLectura: boolean;
  /** Errores de celda en vivo, por llave. */
  errores?: Map<string, Aviso[]>;
  onCambio?: (key: string, c: CambioFila) => void;
  // Sin `agregadas` ni `onQuitarAgregada` desde el 21-oct-2026: «Vendieron
  // aquí» salió de la pantalla (decisión del usuario) y ya no hay renglones
  // agregados sin guardar. Lo guardado con ese origen lleva su distintivo.
  /** Tiendas que se pueden apoyar: el catálogo de `mi-alcance` (el mismo con que la API valida). */
  catalogo?: TiendaCatalogo[];
  diasApoyoMin?: number;
  referencia?: { desde: string; hasta: string };
  /**
   * Suplente en desarrollo (fase 3): abre el diálogo de la persona. Sin él no
   * hay acción, sólo el distintivo (la revisión del regional). NO depende de
   * `soloLectura`: el suplente es del catálogo de la tienda, no de la captura
   * de la semana, y la API lo acepta aunque la semana ya no se pueda editar.
   */
  onSuplente?: (f: FilaVista) => void;
  /**
   * El primer lunes que la cuenta puede tocar (`minimoDeAlcance`); `null` =
   * administrador. Con él no se ofrece «Corregir certificación» cuando la API
   * lo negaría.
   */
  minimoCertificacion?: string | null;
  /**
   * La vista en pantalla ya no es la de la tienda elegida (carga otra): el menú
   * ⋮ y «+ Apoyo» se apagan y ningún diálogo de fila se abre. Con ellos activos,
   * «Suplente en desarrollo…» abría el diálogo de la tienda que se dejaba.
   */
  inactiva?: boolean;
  /**
   * Pendientes de catálogo de cada persona (fase 4), por llave: se marcan bajo
   * su nombre con ícono y texto; los que bloquean el envío, en rojo.
   */
  pendientesCatalogo?: Map<string, PendienteDeTienda[]>;
  /**
   * La tienda pasó por el gerente y está con el supervisor o después
   * (`pasoPorGerente`, 21-oct-2026): bajo la meta que el supervisor cambió se
   * lee «El gerente puso $X».
   */
  conGerente?: boolean;
  /**
   * «Quitar la Casa» (ronda F, 23-oct-2026): bajo su nombre. Sin él (sólo
   * lectura, la cuenta no puede o una API anterior) no hay botón.
   */
  onQuitarCasa?: () => void;
}

const SIN_PENDIENTES = new Map<string, PendienteDeTienda[]>();

/**
 * Los pendientes de catálogo de una fila que vale la pena marcar. «Alta sin
 * clave de vendedor» ya se ve como «Sin clave de vendedor» junto al número: no
 * se repite (su porqué va en el `title` de esa etiqueta).
 */
export function pendientesAMarcar(f: Pick<FilaVista, 'agnum'>, ps: readonly PendienteDeTienda[] | undefined): PendienteDeTienda[] {
  return (ps ?? []).filter((p) => !(p.tipo === 'ALTA_SIN_CLAVE_VENDEDOR' && !f.agnum && !p.bloquea));
}

/**
 * La acción de suplente de una persona, según su programa abierto, o `null` si
 * no hay ninguna que la API vaya a aceptar: corregir una certificación que ya
 * dejó de pagar semanas cerradas responde 409 con cualquier fecha.
 */
export function accionSuplente(f: FilaVista, minimo?: string | null): { texto: string; aria: string } | null {
  const s = f.suplente;
  if (s?.estado === 'autorizada') {
    if (!s.certificadoEn) return { texto: 'Certificar', aria: `Certificar a ${f.nombre}` };
    return puedeCorregirCertificacion(s.certificadoEn, minimo ?? null)
      ? { texto: 'Corregir certificación', aria: `Corregir la certificación de ${f.nombre}` }
      : null;
  }
  return { texto: 'Suplente en desarrollo…', aria: `Suplente en desarrollo: ${f.nombre}` };
}

/** Quita el «NOMBRE: » del mensaje cuando ya se muestra dentro de su renglón. */
const sinNombre = (mensaje: string, nombre: string): string =>
  mensaje.startsWith(`${nombre}: `) ? mensaje.slice(nombre.length + 2) : mensaje;

const vacioSi = (v: string | null | undefined) => (v && v.trim() ? v : '—');

const SIN_CATALOGO: TiendaCatalogo[] = [];
const SIN_MOTIVOS: OpcionMotivo[] = [];

/** Una opción de la lista del motivo: los motivos y «— Sin motivo» (`valor: null`). */
type OpcionMotivoFila = { valor: MotivoSinMeta | null; etiqueta: string };

/**
 * Anchos de la tabla de escritorio (px; la columna del colaborador se queda con
 * el resto). «Sin meta · motivo» mide lo que pide el motivo más largo («Apoyo en
 * otra tienda»: 130 px a 14 px, medido en Chromium) con 12 + 4 de relleno, la
 * flecha de 32, el borde y los 24 de la celda: 204. Con 172 la casilla medía 148
 * y «Vacaciones» salía «Vacacio…». Se toma de «Puesto», «Alta» y la meta (156 en
 * el diseño; «$ 1,000,000» cabe en 140); a 1366 px la tabla mide ~1188 y al
 * colaborador le siguen quedando ~200, sin scroll horizontal.
 *
 * 21-oct-2026 (decisión del usuario: transporte, bodeguero fijo y días de
 * incapacidad se capturan POR PERSONA): «Puesto» y «Alta» van en una sola
 * columna (el alta, en el segundo renglón) y sus 88 px, con los 10 que
 * sobraban, son la columna «Bonos e incapacidad» (98, con relleno de 8): la
 * suma de las fijas sigue en 998 y al colaborador le quedan los mismos ~190.
 */
export const ANCHOS_TABLA = {
  estado: 40,
  puesto: 112,
  venta: 100,
  metaAnterior: 100,
  meta: 140,
  vs: 76,
  motivo: 208,
  marcas: 98,
  acciones: 124,
  accionesCompacta: 84,
} as const;

/** «Bonos e incapacidad»: lo que se captura por persona además de la meta (21-oct-2026). */
export const TITULO_MARCAS =
  'Transporte y bodeguero fijo (bodega de más de un piso; sólo puestos de bodega) se pagan por persona. Los días de incapacidad sólo informan: no cambian la meta.';

/** La opción que QUITA el motivo (la del `select` del diseño, «—»). */
export const OPCION_SIN_MOTIVO: OpcionMotivoFila = { valor: null, etiqueta: '— Sin motivo' };

/** Días con que abre un apoyo nuevo (decisión del 29-sep: la semana completa). */
export const DIAS_APOYO_POR_OMISION = 7;

/**
 * La venta de referencia de la fila: la venta semanal PROMEDIO de 8 semanas
 * cerradas en todas sus tiendas (`ventaPromedio`; `null` = «—»). Una API
 * anterior no la manda: ahí se usa la de 4 semanas de esta tienda.
 */
export const ventaReferencia = (f: FilaVista): number | null =>
  f.ventaPromedio !== undefined ? f.ventaPromedio : f.ventaRefSemanal;

/**
 * «vs. prom.» EN VIVO: `meta / venta promedio − 1` (SPEC §6). Sin meta, o sin
 * promedio (menos de 2 semanas, o $0), no hay comparación: `null` («—»).
 */
export function vsPromedio(f: FilaVista): number | null {
  const prom = ventaReferencia(f);
  if (f.metaMonto === null || f.metaMonto === undefined || !(f.metaMonto > 0)) return null;
  if (prom === null || prom === undefined || !(prom > 0)) return null;
  return f.metaMonto / prom - 1;
}

/**
 * Su puesto no lleva meta individual: la casilla de la META es de sólo lectura.
 * El motivo (vacaciones, incapacidad…) y el apoyo SÍ se capturan: sin ellos una
 * cajera de vacaciones cobraba como cajera en silencio y el bono de APOYO de
 * cajeros y bodegueros se perdía (revisión del 30-sep).
 */
export const noLlevaMeta = (f: FilaVista): boolean => f.reglaMeta === 'no_lleva';

/**
 * La casilla de la meta está BLOQUEADA: su puesto no la lleva y no es de bodega.
 * Al bodeguero sí se le puede poner (23-oct-2026, decisión del usuario: cuando la
 * tienda no tiene vendedores, cobra en caja): opcional, con la advertencia
 * «Se le está colocando meta al bodeguero». Al cajero, no.
 */
export const metaBloqueada = (f: FilaVista): boolean => noLlevaMeta(f) && !metaOpcionalDeBodega(f);

/** Lo que explica la casilla de la meta de un bodeguero (`title`). */
export const TITULO_META_BODEGUERO = `Opcional: ponle meta sólo si esta semana cobra en caja (por ejemplo, la tienda no tiene vendedores). Sale «${TEXTO_META_A_BODEGUERO}» y el regional lo ve; no impide enviar.`;

/** «Sin meta individual · Cajera»: lo que se ve en la casilla de la meta de una fila `no_lleva`. */
export const textoNoLleva = (f: FilaVista): string =>
  `Sin meta individual · ${f.puesto && f.puesto.trim() ? f.puesto.trim() : 'su puesto'}`;

/** Errores de celda que son de la casilla de la meta (se pinta en rojo). */
const ERRORES_DE_META = ['META_NO_POSITIVA', 'META_FUERA_DE_LIMITE', 'META_Y_MOTIVO'];
/** Errores de celda que son de la casilla del motivo (sin el detalle de «Otro», que tiene su casilla). */
const ERRORES_DE_MOTIVO = ['MOTIVO_RETIRADO', 'MOTIVO_DESCONOCIDO', 'APOYO_REQUERIDO'];

const TONO_DISTINTIVO: Record<SeveridadTag, string> = {
  success: 'bg-metas-exito-fondo text-metas-exito',
  info: 'bg-metas-info-fondo text-metas-info',
  warning: 'bg-metas-aviso-fondo text-metas-aviso',
  danger: 'bg-metas-error-fondo text-metas-error',
  secondary: 'bg-metas-neutro-fondo text-metas-neutro',
};

/** La etiqueta chica del diseño (`.tag`: 20 px, radio 6). */
const Etiqueta: React.FC<{ tono: SeveridadTag; title?: string; children: React.ReactNode }> = ({ tono, title, children }) => (
  <span
    title={title}
    className={cx('inline-flex h-5 items-center whitespace-nowrap rounded-md px-2 text-[11px] font-semibold', TONO_DISTINTIVO[tono])}
  >
    {children}
  </span>
);

/** Una de las tres cifras de la tarjeta del teléfono (VENTA PROM., META ANTERIOR, VS. PROM.). */
const Cifra: React.FC<{ etiqueta: string; children: React.ReactNode; title?: string }> = ({ etiqueta, children, title }) => (
  <div className="flex min-w-0 flex-col gap-0.5 text-sm" title={title}>
    <span className="text-[11px] font-semibold uppercase tracking-[.03em] text-metas-texto-2">{etiqueta}</span>
    <span className="num truncate">{children}</span>
  </div>
);

type Situacion = 'listo' | 'pendiente' | 'error' | 'puesto' | 'casa';

const Icono: React.FC<{ situacion: Situacion; tamano?: number }> = ({ situacion, tamano = 20 }) => {
  const titulo = {
    listo: 'Listo',
    pendiente: 'Falta meta o motivo',
    error: 'Hay un error en el renglón',
    puesto: 'Sin meta por su puesto',
    casa: 'La Casa sin meta (opcional)',
  }[situacion];
  const color = {
    listo: 'text-metas-exito',
    pendiente: 'text-metas-aviso-icono',
    error: 'text-metas-error',
    puesto: 'text-metas-texto-2',
    casa: 'text-metas-texto-2',
  }[situacion];
  return (
    <span title={titulo} className={cx('flex', color)} data-situacion={situacion}>
      <svg width={tamano} height={tamano} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        {situacion === 'listo' && <path d="M8 12l3 3 5-6" />}
        {(situacion === 'pendiente' || situacion === 'error') && <path d="M12 7v6M12 16.5h.01" />}
        {(situacion === 'puesto' || situacion === 'casa') && <path d="M8 12h8" />}
      </svg>
      <span className="sr-only">{titulo}</span>
    </span>
  );
};

/**
 * La captura de una tienda: una fila por colaborador (diseño `Main` y
 * `MovilCaptura`).
 *
 * Tres disposiciones, según el ancho:
 * - teléfono (< 768 px): una TARJETA por persona, con tres cifras, meta y
 *   motivo de 44 px y lo demás en su menú ⋮. Regla del panel: una tabla de más
 *   de 3 columnas en teléfono va en tarjetas, nunca con scroll lateral.
 * - intermedia (< 1366 px): puesto, alta, venta y meta anterior dentro de la
 *   celda del colaborador, «vs. prom.» bajo la meta y el apoyo en el menú ⋮,
 *   para que quepa sin arrastrar la página de lado.
 * - escritorio: cada dato en su columna, como el diseño.
 *
 * Meta y motivo se EXCLUYEN (SPEC §8.3): con motivo, la meta queda apagada; con
 * meta, el motivo. Para cambiar de uno a otro se borra el que hay (la opción
 * «— Sin motivo» de la lista, o la casilla vacía). `aplicarCambio` además limpia
 * el otro por si acaso.
 *
 * Es una `<table>` real: la usan también la revisión del regional
 * (`CapturaLecturaDialog`, en sólo lectura) y el lector de pantalla recorre sus
 * columnas por nombre.
 */
export const ColaboradoresTabla: React.FC<Props> = ({
  filas,
  tienda,
  motivos,
  motivosHistoricos = SIN_MOTIVOS,
  soloLectura,
  errores,
  onCambio,
  catalogo = SIN_CATALOGO,
  diasApoyoMin = 3,
  referencia,
  onSuplente,
  minimoCertificacion,
  inactiva = false,
  pendientesCatalogo = SIN_PENDIENTES,
  conGerente = false,
  onQuitarCasa,
}) => {
  // Tarjetas hasta 1023 px (auditoría del 22-oct-2026): a 768 la tabla compacta
  // dejaba ~60 px al colaborador y partía los nombres por sílabas.
  const esMovil = useEsMovil(1023);
  const compacta = useEsMovil(1365);
  // La explicación de «Bonos e incapacidad» para el lector de pantalla: un `title`
  // no se lee con teclado ni con el dedo (revisión de la fase B, 21-oct-2026).
  const idTituloMarcas = useId();
  const [apoyoDeEstado, setApoyoDe] = useState<FilaVista | null>(null);
  const [historialDeEstado, setHistorialDe] = useState<FilaVista | null>(null);
  // Mientras carga otra tienda no se abre ni queda abierto ningún diálogo de fila.
  const apoyoDe = inactiva ? null : apoyoDeEstado;
  const historialDe = inactiva ? null : historialDeEstado;
  useEffect(() => {
    if (!inactiva) return;
    setApoyoDe(null);
    setHistorialDe(null);
  }, [inactiva]);

  const cambiar = (key: string, c: CambioFila) => onCambio?.(key, c);
  const todosLosMotivos = [...motivos, ...motivosHistoricos];
  const opcionesMotivo: OpcionMotivoFila[] = [OPCION_SIN_MOTIVO, ...motivos];
  const etiquetaMotivo = (v: string | null | undefined) =>
    todosLosMotivos.find((m) => m.valor === v)?.etiqueta ?? (v ? ETIQUETA_MOTIVO[v as MotivoSinMeta] ?? v : '—');
  const tituloReferencia =
    filas.some((f) => f.ventaPromedio !== undefined) || !referencia
      ? 'Venta semanal promedio de las últimas 8 semanas cerradas, en todas sus tiendas, sin IVA. No cuentan las semanas de vacaciones, incapacidad, capacitación o baja en proceso. «—» = menos de 2 semanas con datos.'
      : `Venta promedio por semana de las 4 semanas anteriores (${fechaCorta(referencia.desde)} a ${fechaCorta(referencia.hasta)}), sin IVA`;

  // La API trae las marcas por persona (fase B, 21-oct-2026): hay casillas y columna.
  const conMarcas = filas.some(conMarcasPorPersona);

  const situacion = (f: FilaVista): Situacion => {
    if (errores?.has(f.key)) return 'error';
    if (esPendiente(f)) return 'pendiente';
    if ((f.metaMonto !== null && f.metaMonto !== undefined) || f.sinMetaMotivo) return 'listo';
    return esFilaCasa(f) ? 'casa' : 'puesto';
  };

  // ------------------------------------------------------------ piezas
  // ⚠️ Funciones que devuelven JSX, NO componentes declarados aquí dentro: un
  // componente definido dentro del render es un tipo NUEVO en cada render,
  // React lo desmonta y lo vuelve a montar, y la casilla de la meta perdería el
  // foco a cada tecla.

  /** Lo de la semana anterior: su meta o, si no llevó, su motivo. */
  const semanaAnterior = (f: FilaVista) => {
    if (f.metaSemanaAnterior !== null && f.metaSemanaAnterior !== undefined) return pesosEnteros(f.metaSemanaAnterior);
    if (f.motivoSemanaAnterior)
      return (
        <span title={f.detalleSemanaAnterior ?? undefined} className="text-metas-texto-2">
          {etiquetaMotivo(f.motivoSemanaAnterior)}
        </span>
      );
    return '—';
  };

  const vs = (f: FilaVista) => {
    const x = vsPromedio(f);
    if (x === null) return <span className="text-metas-texto-2">—</span>;
    const texto = pctConSigno(x);
    const tono = texto.startsWith('+') ? 'font-semibold text-metas-exito' : texto.startsWith('−') ? 'font-semibold text-metas-error' : '';
    return (
      <span className={cx('num whitespace-nowrap', tono)} title="Meta de la semana contra su venta semanal promedio">
        {texto}
      </span>
    );
  };

  /** Lo de abajo del nombre: pendientes de catálogo, distintivos y errores. */
  const extras = (f: FilaVista) => {
    const errs = errores?.get(f.key) ?? [];
    const distintivos = distintivosDe(f);
    const pendientes = pendientesAMarcar(f, pendientesCatalogo.get(f.key));
    return (
      <>
        {pendientes.map((p) => (
          <span
            key={p.tipo}
            className={cx(
              'inline-flex items-start gap-1 text-xs font-semibold [overflow-wrap:anywhere]',
              p.bloquea ? 'text-metas-error' : 'text-metas-aviso',
            )}
            title={`${p.detalle} Lo corrige ${p.area}.`}
            data-testid={`pendiente-catalogo-${f.key}`}
            data-bloquea={p.bloquea || undefined}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
              {p.bloquea ? (
                <>
                  <rect x="5" y="11" width="14" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </>
              ) : (
                <>
                  <path d="M12 3l10 18H2z" />
                  <path d="M12 10v4M12 17.5h.01" />
                </>
              )}
            </svg>
            {p.bloquea ? `${p.etiqueta}: bloquea el envío` : p.etiqueta}
          </span>
        ))}
        {distintivos.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {distintivos.map((d) => (
              <Etiqueta key={d.texto} tono={d.severidad} title={d.detalle}>
                {d.texto}
              </Etiqueta>
            ))}
          </div>
        )}
        {errs.map((e) => (
          <div key={e.codigo} className="text-xs font-semibold text-metas-error" role="alert">
            {sinNombre(e.mensaje, f.nombre)}
          </div>
        ))}
      </>
    );
  };

  /** Avatar, nombre y «N° 22886 · Clave 4PK» (y en la intermedia, puesto, alta y referencia). */
  const identidad = (f: FilaVista) => {
    const datos = [f.numero !== null ? `N° ${f.numero}` : null, f.agnum ? `Clave ${f.agnum}` : null].filter(Boolean);
    return (
      <div className="flex min-w-0 items-start gap-3">
        <Avatar nombre={f.nombre} />
        <div className="flex min-w-0 flex-col gap-1">
          {/* Completo, en dos líneas si hace falta: cortado («Christopher Emmanuel Her…») no
              se sabía de quién era la meta (revisión de la fase B, 21-oct-2026). */}
          <span className="font-semibold text-metas-texto [overflow-wrap:anywhere]" title={f.nombre}>
            {f.nombre}
          </span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-metas-texto-2">
            {datos.length > 0 && <span className="num">{datos.join(' · ')}</span>}
            {!f.agnum && <Etiqueta tono="warning">Sin clave de vendedor</Etiqueta>}
          </span>
          {compacta && (
            <span className="num text-xs text-metas-texto-2">
              {[vacioSi(f.puesto), f.fechaAlta ? `alta ${fechaCorta(f.fechaAlta)}` : null].filter(Boolean).join(' · ')}
              <br />
              <span title={tituloReferencia}>Venta prom. {pesosEnteros(ventaReferencia(f))}</span> · Sem. ant.{' '}
              {semanaAnterior(f)}
              {f.apoyo && (
                <>
                  <br />
                  Apoyo: {f.apoyo.chain} {f.apoyo.num} · {f.apoyo.dias} d
                </>
              )}
            </span>
          )}
          {compacta && marcas(f, 'linea')}
          {extras(f)}
        </div>
      </div>
    );
  };

  /**
   * La Casa (ronda F, 23-oct-2026): no es una persona. Sin avatar, número ni
   * clave; bajo el nombre, qué es, sus distintivos y «Quitar la Casa» (44 px en
   * el teléfono y en pantalla táctil). Sólo lleva meta.
   */
  const identidadCasa = (f: FilaVista, tarjeta = false) => (
    <div className="flex min-w-0 items-start gap-3" data-testid="fila-casa">
      <span
        aria-hidden="true"
        className={cx('flex shrink-0 items-center justify-center rounded-full bg-metas-neutro-fondo text-metas-neutro', tarjeta ? 'h-10 w-10' : 'h-9 w-9')}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 11l9-7 9 7" />
          <path d="M5 10v10h14V10" />
          <path d="M10 20v-6h4v6" />
        </svg>
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className={cx('font-semibold text-metas-texto [overflow-wrap:anywhere]', tarjeta && 'text-[15px]')} title={f.nombre}>
          {f.nombre}
        </span>
        <span className="text-xs text-metas-texto-2" data-testid="descripcion-casa">
          {DESCRIPCION_CASA}
        </span>
        {extras(f)}
        {!soloLectura && onQuitarCasa && (
          <button
            type="button"
            className="metas-foco inline-flex items-center self-start text-xs font-semibold text-metas-primario underline disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-metas-tactil max-md:px-1 [@media(pointer:coarse)]:min-h-metas-tactil"
            onClick={onQuitarCasa}
            disabled={inactiva}
            title="Quita el renglón de la Casa de esta semana (su meta deja de contar para la tienda)"
          >
            Quitar la Casa
          </button>
        )}
      </div>
    </div>
  );

  const estadoCampoMeta = (f: FilaVista): EstadoCampo =>
    f.sinMetaMotivo ? 'off' : esPendiente(f) && !soloLectura ? 'aviso' : 'normal';

  const meta = (f: FilaVista, grande = false) => {
    const sinValor = f.metaMonto === null || f.metaMonto === undefined;
    // El bodeguero sin meta, en sólo lectura, se sigue leyendo «Sin meta individual»;
    // para capturar, su casilla se abre (opcional) como la de cualquiera.
    if (noLlevaMeta(f) && sinValor && (metaBloqueada(f) || soloLectura))
      return (
        <span
          className="text-xs text-metas-texto-2"
          data-testid="sin-meta-individual"
          title="Su puesto no lleva meta individual: cobra por su propio esquema."
        >
          {textoNoLleva(f)}
        </span>
      );
    // Una meta que ya estaba guardada cuando su puesto pasó a no llevarla (BUK a
    // veces va atrasado): la API la conserva, pero el supervisor debe poder
    // quitarla si BUK tiene razón. Escribir una nueva no: sería 422 META_NO_LLEVA.
    if (metaBloqueada(f) && !soloLectura)
      return (
        <div className="flex flex-col gap-1" data-testid="meta-no-lleva-guardada">
          <span className="num font-semibold" title="Su puesto ya no lleva meta individual; esta meta se guardó antes.">
            {pesos(f.metaMonto as number)}
          </span>
          <button
            type="button"
            className="metas-foco inline-flex items-center self-start text-xs font-semibold text-metas-primario underline max-md:min-h-metas-tactil max-md:px-1 [@media(pointer:coarse)]:min-h-metas-tactil"
            onClick={() => cambiar(f.key, { campo: 'metaMonto', valor: null })}
            aria-label={`Quitar la meta de ${f.nombre}`}
          >
            Quitar meta
          </button>
        </div>
      );
    if (soloLectura || metaBloqueada(f))
      return <span className="num font-semibold">{f.metaMonto !== null ? pesos(f.metaMonto) : '—'}</span>;
    const deBodega = metaOpcionalDeBodega(f);
    const conError = (errores?.get(f.key) ?? []).some((e) => ERRORES_DE_META.includes(e.codigo));
    const apagada = !!f.sinMetaMotivo;
    return (
      <label
        className={cx(
          'metas-campo w-full focus-within:border-metas-primario focus-within:shadow-metas-anillo-sel',
          CLASES_CAMPO[estadoCampoMeta(f)],
          grande && 'metas-campo-grande !h-metas-tactil',
          conError && '!border-metas-error',
        )}
        title={
          apagada
            ? 'Tiene motivo: elige «— Sin motivo» para escribir una meta.'
            : deBodega
              ? TITULO_META_BODEGUERO
              : esFilaCasa(f)
                ? TITULO_META_CASA
                : undefined
        }
        data-meta-bodeguero={deBodega || undefined}
      >
        <span className="text-metas-texto-2" aria-hidden="true">
          $
        </span>
        <InputNumber
          value={f.metaMonto}
          // `onChange` avisa en CADA tecla; `onValueChange` sólo al salir de la
          // casilla. Con sólo el segundo, el primer clic en «Guardar borrador»
          // se gastaba en quitar el foco. Se quedan los dos: el de salida
          // normaliza lo que se pegó o se dejó a medias.
          onChange={(e) => cambiar(f.key, { campo: 'metaMonto', valor: e.value ?? null })}
          onValueChange={(e) => cambiar(f.key, { campo: 'metaMonto', valor: e.value ?? null })}
          mode="decimal"
          locale="es-MX"
          // ⚠️ Pesos ENTEROS. Con decimales y `onChange` en cada tecla, borrar el
          // último decimal de «95000.5» devolvía 95000 a la casilla y el
          // siguiente dígito caía en los enteros: 950 000, diez veces la meta.
          minFractionDigits={0}
          maxFractionDigits={0}
          min={0}
          inputMode="numeric"
          disabled={apagada}
          placeholder={f.reglaMeta === 'opcional' || deBodega ? 'Opcional' : '0'}
          className="metas-campo-numero"
          inputClassName={cx('metas-campo-input', conError && 'p-invalid')}
          aria-label={`Meta de ${f.nombre}`}
        />
      </label>
    );
  };

  const sinMeta = (f: FilaVista, grande = false) => {
    // La Casa sólo lleva meta (ronda F): ni motivo, ni apoyo.
    if (esFilaCasa(f))
      return (
        <span className="text-xs text-metas-texto-2" data-testid="casa-solo-meta">
          Sólo meta
        </span>
      );
    if (soloLectura) {
      if (!f.sinMetaMotivo) return <span className="text-metas-texto-2">—</span>;
      return (
        <span>
          {etiquetaMotivo(f.sinMetaMotivo)}
          {f.sinMetaDetalle ? <span className="text-metas-texto-2"> · {f.sinMetaDetalle}</span> : null}
        </span>
      );
    }
    const errsFila = errores?.get(f.key) ?? [];
    const otroSinDetalle = errsFila.some((e) => e.codigo === 'OTRO_SIN_DETALLE');
    const motivoConError = errsFila.some((e) => ERRORES_DE_MOTIVO.includes(e.codigo));
    // Un motivo HISTÓRICO guardado («Cajero/a», «Gerente») ya no se elige: se
    // muestra como texto y sólo se puede quitar (también se quita al poner meta).
    if (esMotivoHistorico(f.sinMetaMotivo))
      return (
        <div className="flex flex-col gap-1" data-testid="motivo-historico">
          <span title="Motivo guardado antes del 29-sep. Ya no se captura: quién lleva meta lo dice su puesto de BUK.">
            {etiquetaMotivo(f.sinMetaMotivo)} <span className="text-xs text-metas-texto-2">(ya no se captura)</span>
          </span>
          <button
            type="button"
            className="metas-foco inline-flex items-center self-start text-xs font-semibold text-metas-primario underline max-md:min-h-metas-tactil max-md:px-1 [@media(pointer:coarse)]:min-h-metas-tactil"
            onClick={() => cambiar(f.key, { campo: 'sinMetaMotivo', valor: null })}
            aria-label={`Quitar el motivo de ${f.nombre}`}
          >
            Quitar
          </button>
        </div>
      );
    const conMeta = f.metaMonto !== null && f.metaMonto !== undefined && !metaBloqueada(f);
    return (
      <div className="flex w-full flex-col gap-1" title={conMeta ? 'Tiene meta: bórrala para elegir un motivo.' : undefined}>
        <Dropdown
          value={f.sinMetaMotivo}
          options={opcionesMotivo}
          optionLabel="etiqueta"
          optionValue="valor"
          onChange={(e) => {
            const valor = (e.value ?? null) as MotivoSinMeta | null;
            cambiar(f.key, { campo: 'sinMetaMotivo', valor });
            // «Apoyo en otra tienda» exige decir a cuál: se abre el diálogo.
            if (valor === 'apoyo_otra_tienda' && !f.apoyo) setApoyoDe(f);
          }}
          // Sin `showClear`: su ✕ tapaba el texto («Vacacione✕»), medía 16 px en el
          // teléfono y se anunciaba «Clear». Se quita con «— Sin motivo», como el
          // `select` del diseño.
          disabled={conMeta}
          placeholder="—"
          // Sin «…»: si el motivo no cabe (teléfono angosto), salta de renglón
          // legible, y el `title` lo dice completo.
          valueTemplate={(o: OpcionMotivoFila | null) => (
            <span className="block min-w-0 whitespace-normal break-words" title={o && o.valor ? o.etiqueta : undefined}>
              {o && o.valor ? o.etiqueta : '—'}
            </span>
          )}
          // El disparador (la flecha) no recibe foco y PrimeReact lo nombra con
          // el placeholder: un botón «—» por fila. Lo nombrado es el combobox.
          pt={{ trigger: { 'aria-hidden': true } }}
          // En el teléfono la lista medía 200 px: se veían 4 de los 8 motivos
          // («Apoyo en otra tienda», «Baja en proceso», «Tienda errónea» y «Otro» quedaban
          // ocultos). Con 320 caben todos (auditoría del 22-oct-2026).
          scrollHeight={grande ? '320px' : undefined}
          className={cx(
            'metas-dropdown-motivo w-full',
            motivoConError && 'p-invalid',
            !motivoConError && esPendiente(f) && 'metas-dropdown-aviso',
            conMeta && 'metas-dropdown-off',
            grande && 'metas-dropdown-grande',
          )}
          panelClassName={CLASE_OVERLAY}
          aria-label={`Motivo sin meta de ${f.nombre}`}
        />
        {f.sinMetaMotivo === 'otro' && (
          <InputText
            value={f.sinMetaDetalle ?? ''}
            onChange={(e) => cambiar(f.key, { campo: 'sinMetaDetalle', valor: e.target.value })}
            placeholder="¿Cuál?"
            maxLength={200}
            className={cx('w-full', otroSinDetalle && 'p-invalid')}
            aria-label={`Detalle del motivo de ${f.nombre}`}
          />
        )}
      </div>
    );
  };

  /** «El gerente puso $X» bajo la meta que el supervisor cambió (en vivo). */
  const ajuste = (f: FilaVista) => {
    const texto = ajusteDelGerente(f, conGerente);
    return texto ? (
      <span className="mt-1 block text-xs font-semibold text-metas-aviso" data-testid={`ajuste-gerente-${f.key}`}>
        {texto}
      </span>
    ) : null;
  };

  /**
   * Transporte, bodeguero fijo y días de incapacidad de la persona (21-oct-2026).
   * «Bodeguero fijo» sólo a un puesto de bodega (o si ya estaba marcado: la API
   * lo conserva si BUK le cambió el puesto, y aquí se puede quitar). Los días de
   * incapacidad SÓLO informan: no cambian la meta.
   * - `columna`: apilado, en su columna (escritorio);
   * - `linea`: en un renglón bajo el colaborador (intermedia);
   * - `tarjeta`: en la tarjeta del teléfono, con blancos táctiles de 44 px.
   */
  const marcas = (f: FilaVista, forma: 'columna' | 'linea' | 'tarjeta') => {
    // La Casa no lleva transporte, bodeguero fijo ni incapacidad (ronda F).
    if (!conMarcasPorPersona(f) || esFilaCasa(f)) return null;
    const bodega = !!f.esBodega || !!f.bodegueroFijo;
    const dias = f.diasIncapacidad ?? null;
    if (soloLectura) {
      const partes = marcasEnPalabras(f);
      // En la columna la etiqueta es su cabecera; en la tarjeta y bajo el
      // colaborador no hay cabecera: sin etiqueta quedaba un «—» suelto
      // (auditoría del 22-oct-2026).
      if (forma !== 'columna')
        return (
          <span className="text-xs text-metas-texto-2" data-testid={`marcas-${f.key}`}>
            Bonos e incapacidad:{' '}
            <span className={partes.length ? 'font-semibold text-metas-texto-3' : undefined}>
              {partes.length ? partes.join(' · ') : 'ninguno'}
            </span>
          </span>
        );
      return (
        <span
          className={cx('flex flex-col gap-0.5 text-xs', partes.length ? 'text-metas-texto-3' : 'text-metas-texto-2')}
          data-testid={`marcas-${f.key}`}
        >
          {partes.length ? partes.map((x) => <span key={x}>{x}</span>) : '—'}
        </span>
      );
    }
    const tactil = forma === 'tarjeta';
    // En pantalla táctil, también en la tabla: la casilla de Transporte medía 16 px
    // a 768 (auditoría del 22-oct-2026); la etiqueta entera es el blanco.
    const caja = cx(
      'inline-flex items-center gap-1.5 whitespace-nowrap',
      tactil ? 'min-h-metas-tactil' : '[@media(pointer:coarse)]:min-h-metas-tactil',
    );
    const casilla = cx(
      'accent-metas-primario disabled:cursor-not-allowed',
      tactil ? 'h-5 w-5' : 'h-4 w-4 [@media(pointer:coarse)]:h-5 [@media(pointer:coarse)]:w-5',
    );
    const texto = forma === 'columna' ? 'text-[11px]' : 'text-xs';
    // El nombre accesible EMPIEZA con lo que se ve (WCAG 2.5.3, revisión de la fase B,
    // 21-oct-2026): en la columna de escritorio se ve abreviado («Bod. fijo», «Incap.»),
    // y quien usa control por voz dice lo que lee.
    const nombreBodeguero = forma === 'columna' ? `Bod. fijo: bodeguero fijo de ${f.nombre}` : `Bodeguero fijo de ${f.nombre}`;
    const nombreIncapacidad =
      forma === 'columna' ? `Incap.: días de incapacidad de ${f.nombre}` : `Incapacidad: días de incapacidad de ${f.nombre}`;
    return (
      <div
        className={cx(
          'flex text-metas-texto-3',
          forma === 'columna' ? 'flex-col gap-1' : 'flex-wrap items-center gap-x-3 gap-y-1',
        )}
        data-testid={`marcas-${f.key}`}
      >
        <label className={caja} title="Apoyo de transporte: se paga por persona.">
          <input
            type="checkbox"
            className={casilla}
            checked={!!f.transporte}
            disabled={inactiva}
            onChange={(e) => cambiar(f.key, { campo: 'transporte', valor: e.target.checked })}
            aria-label={`Transporte de ${f.nombre}`}
          />
          <span className={texto}>Transporte</span>
        </label>
        {bodega && (
          <label className={caja} title="Bodeguero fijo con bodega de más de un piso (sólo puestos de bodega): se paga por persona.">
            <input
              type="checkbox"
              className={casilla}
              checked={!!f.bodegueroFijo}
              disabled={inactiva}
              onChange={(e) => cambiar(f.key, { campo: 'bodegueroFijo', valor: e.target.checked })}
              aria-label={nombreBodeguero}
            />
            <span className={texto}>{forma === 'columna' ? 'Bod. fijo' : 'Bodeguero fijo'}</span>
          </label>
        )}
        <label className={caja} title="Días de incapacidad de la semana. Sólo informa: no cambia la meta (si hace falta, redúcela).">
          <span className={texto}>{forma === 'columna' ? 'Incap.' : 'Incapacidad'}</span>
          <select
            className={cx(
              'metas-foco rounded-metas-control border border-metas-borde-control bg-metas-panel px-1 text-metas-texto disabled:cursor-not-allowed disabled:opacity-50',
              // En la tarjeta, 44 px y letra de 16: con 14 Safari del iPhone acercaba la página.
              tactil ? 'h-11 min-w-[4.5rem] px-2 text-base' : 'h-7 text-xs',
            )}
            value={dias === null ? '' : String(dias)}
            disabled={inactiva}
            onChange={(e) => cambiar(f.key, { campo: 'diasIncapacidad', valor: e.target.value === '' ? null : Number(e.target.value) })}
            aria-label={nombreIncapacidad}
          >
            <option value="">—</option>
            {DIAS_INCAPACIDAD.map((n) => (
              <option key={n} value={String(n)}>
                {n} d
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  };

  const textoApoyo = (f: FilaVista) => (f.apoyo ? `${f.apoyo.chain} ${f.apoyo.num} · ${f.apoyo.dias} d` : null);
  const tituloApoyo = (f: FilaVista) =>
    f.apoyo ? `${f.apoyo.chain} ${f.apoyo.num} · ${f.apoyo.tienda ?? ''} — ${f.apoyo.dias} día(s)` : 'Registrar días de apoyo a otra tienda';

  /** «+ Apoyo» visible (escritorio). En sólo lectura, el texto del apoyo. */
  const botonApoyo = (f: FilaVista) => {
    if (soloLectura)
      return (
        <span className="text-sm" title={f.apoyo ? tituloApoyo(f) : undefined}>
          {textoApoyo(f) ?? ''}
        </span>
      );
    return (
      <button
        type="button"
        onClick={() => setApoyoDe(f)}
        disabled={inactiva}
        aria-label={`Apoyo de ${f.nombre}`}
        title={tituloApoyo(f)}
        className="metas-foco inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-metas-control px-2 text-sm font-semibold text-metas-primario hover:bg-metas-cabecera disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {f.apoyo ? <path d="M5 12h14M13 6l6 6-6 6" /> : <path d="M12 5v14M5 12h14" />}
        </svg>
        <span className="num">{textoApoyo(f) ?? 'Apoyo'}</span>
      </button>
    );
  };

  /** El menú ⋮: suplente, historial, quitar apoyo; en tarjeta e intermedia, también el apoyo. */
  const menu = (f: FilaVista, conApoyo: boolean, tamano: 36 | 44 = 36) => {
    const accion = onSuplente ? accionSuplente(f, minimoCertificacion) : null;
    const acciones: AccionMenu[] = [];
    if (conApoyo && !soloLectura)
      acciones.push({
        id: 'apoyo',
        texto: f.apoyo ? `Apoyo: ${textoApoyo(f)}…` : 'Apoyo en otra tienda…',
        aria: `Apoyo de ${f.nombre}`,
        onSelect: () => setApoyoDe(f),
      });
    if (accion) acciones.push({ id: 'suplente', texto: accion.texto, aria: accion.aria, onSelect: () => onSuplente?.(f) });
    acciones.push({ id: 'historial', texto: 'Historial', aria: `Historial de ${f.nombre}`, onSelect: () => setHistorialDe(f) });
    if (f.apoyo && !soloLectura)
      acciones.push({
        id: 'quitar-apoyo',
        texto: 'Quitar apoyo',
        aria: `Quitar el apoyo de ${f.nombre}`,
        peligro: true,
        onSelect: () => cambiar(f.key, { campo: 'apoyo', valor: null }),
      });
    return <MenuAcciones etiqueta={`Más acciones: ${f.nombre}`} acciones={acciones} tamano={tamano} disabled={inactiva} />;
  };

  const dialogos = (
    <>
      {!soloLectura && (
        <ApoyoDialog
          fila={apoyoDe}
          tiendaPropia={tienda}
          catalogo={catalogo}
          diasApoyoMin={diasApoyoMin}
          diasPorOmision={DIAS_APOYO_POR_OMISION}
          onHide={() => setApoyoDe(null)}
          onGuardar={(apoyo) => {
            if (apoyoDe) cambiar(apoyoDe.key, { campo: 'apoyo', valor: apoyo });
            setApoyoDe(null);
          }}
        />
      )}
      <HistorialColaboradorDialog fila={historialDe} motivos={todosLosMotivos} onHide={() => setHistorialDe(null)} />
    </>
  );

  // ------------------------------------------------------------ teléfono
  if (esMovil) {
    return (
      <>
        <ul className="m-0 flex list-none flex-col gap-3 p-0" data-testid="colaboradores-tarjetas">
          {filas.map((f) => {
            const s = situacion(f);
            const pend = s === 'pendiente' && !soloLectura;
            if (esFilaCasa(f))
              // La Casa (ronda F): su nombre, qué es, la meta (opcional) y «Quitar la Casa».
              return (
                <li
                  key={f.key}
                  className={cx(
                    'flex flex-col gap-3 rounded-metas-panel border border-dashed p-3.5',
                    s === 'error' ? 'border-metas-error bg-metas-panel' : 'border-metas-borde bg-metas-panel',
                  )}
                  data-casa=""
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-grow">{identidadCasa(f, true)}</div>
                    <Icono situacion={s} tamano={22} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-[11px] font-semibold uppercase tracking-[.03em] text-metas-texto-2">Meta semana</span>
                      {meta(f, true)}
                    </div>
                    <Cifra etiqueta="Meta anterior">{semanaAnterior(f)}</Cifra>
                  </div>
                </li>
              );
            return (
              <li
                key={f.key}
                data-pendiente={pend || undefined}
                className={cx(
                  'flex flex-col gap-3 rounded-metas-panel border p-3.5',
                  s === 'error'
                    ? 'border-metas-error bg-metas-panel'
                    : pend
                      ? 'border-metas-tarjeta-pendiente-borde bg-metas-tarjeta-pendiente'
                      : 'border-metas-borde bg-metas-panel',
                )}
              >
                <div className="flex items-center gap-3">
                  <Avatar nombre={f.nombre} />
                  <div className="flex min-w-0 flex-grow flex-col gap-0.5">
                    <span className="text-[15px] font-semibold [overflow-wrap:anywhere]" title={f.nombre}>
                      {f.nombre}
                    </span>
                    <span className="num text-xs text-metas-texto-2">
                      {[vacioSi(f.puesto), f.numero !== null ? `N° ${f.numero}` : null].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  <Icono situacion={s} tamano={22} />
                  {menu(f, true, 44)}
                </div>
                {(distintivosDe(f).length > 0 ||
                  (errores?.get(f.key) ?? []).length > 0 ||
                  !f.agnum ||
                  pendientesAMarcar(f, pendientesCatalogo.get(f.key)).length > 0) && (
                  <div className="flex flex-col gap-1">
                    {!f.agnum && (
                      <div>
                        <Etiqueta tono="warning">Sin clave de vendedor</Etiqueta>
                      </div>
                    )}
                    {extras(f)}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <Cifra etiqueta="Venta prom." title={tituloReferencia}>
                    {pesosEnteros(ventaReferencia(f))}
                  </Cifra>
                  <Cifra etiqueta="Meta anterior">{semanaAnterior(f)}</Cifra>
                  <Cifra etiqueta="vs. prom.">{vs(f)}</Cifra>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-[11px] font-semibold uppercase tracking-[.03em] text-metas-texto-2">Meta semana</span>
                    {meta(f, true)}
                    {ajuste(f)}
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-[11px] font-semibold uppercase tracking-[.03em] text-metas-texto-2">Sin meta · motivo</span>
                    {sinMeta(f, true)}
                  </div>
                </div>
                {f.apoyo && (
                  <span className="num text-xs text-metas-texto-2" title={tituloApoyo(f)}>
                    Apoyo: {textoApoyo(f)}
                  </span>
                )}
                {marcas(f, 'tarjeta')}
              </li>
            );
          })}
          {!filas.length && <li className="text-sm text-metas-texto-2">Sin colaboradores.</li>}
        </ul>
        {dialogos}
      </>
    );
  }

  // ------------------------------------------------------------ tabla
  const th = 'px-3 py-3 text-left align-bottom text-metas-th text-metas-texto-2';
  const td = 'px-3 py-3 align-middle';
  const der = 'text-right';
  return (
    <>
      <table className="w-full table-fixed border-collapse text-metas-celda" data-testid="colaboradores-tabla">
        <colgroup>
          <col style={{ width: ANCHOS_TABLA.estado }} />
          <col />
          {!compacta && <col style={{ width: ANCHOS_TABLA.puesto }} />}
          {!compacta && <col style={{ width: ANCHOS_TABLA.venta }} />}
          {!compacta && <col style={{ width: ANCHOS_TABLA.metaAnterior }} />}
          <col style={{ width: ANCHOS_TABLA.meta }} />
          {!compacta && <col style={{ width: ANCHOS_TABLA.vs }} />}
          <col style={{ width: ANCHOS_TABLA.motivo }} data-columna="motivo" />
          {!compacta && conMarcas && <col style={{ width: ANCHOS_TABLA.marcas }} data-columna="marcas" />}
          <col style={{ width: compacta ? ANCHOS_TABLA.accionesCompacta : ANCHOS_TABLA.acciones }} />
        </colgroup>
        <thead>
          <tr className="bg-metas-cabecera">
            <th scope="col" className={th}>
              <span className="sr-only">Estado</span>
            </th>
            <th scope="col" className={th}>
              Colaborador
            </th>
            {!compacta && (
              <th scope="col" className={th}>
                Puesto · alta
              </th>
            )}
            {!compacta && (
              <th scope="col" className={cx(th, der)}>
                <span title={tituloReferencia}>Venta sem. prom.</span>
              </th>
            )}
            {!compacta && (
              <th scope="col" className={cx(th, der)}>
                Meta sem. anterior
              </th>
            )}
            <th scope="col" className={th}>
              Meta semana (sin IVA)
            </th>
            {!compacta && (
              <th scope="col" className={cx(th, der)}>
                vs. prom.
              </th>
            )}
            <th scope="col" className={th}>
              Sin meta · motivo
            </th>
            {!compacta && conMarcas && (
              <th scope="col" className={cx(th, 'px-2')} aria-describedby={idTituloMarcas}>
                <span title={TITULO_MARCAS}>Bonos e incapacidad</span>
              </th>
            )}
            <th scope="col" className={cx(th, der)}>
              Acciones
            </th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const s = situacion(f);
            if (esFilaCasa(f))
              // La Casa (ronda F): las mismas columnas, sólo con la meta; lo demás, «—».
              return (
                <tr key={f.key} className={cx('border-t border-metas-borde-suave', s === 'error' && 'bg-metas-error-fondo')} data-casa="">
                  <td className={cx(td, 'text-center')}>
                    <div className="flex justify-center">
                      <Icono situacion={s} />
                    </div>
                  </td>
                  <td className={td}>{identidadCasa(f)}</td>
                  {!compacta && (
                    <td className={td}>
                      <span className="block">{vacioSi(f.puesto)}</span>
                    </td>
                  )}
                  {!compacta && <td className={cx(td, der, 'num text-metas-texto-2')}>—</td>}
                  {!compacta && <td className={cx(td, der, 'num text-metas-texto-2')}>{semanaAnterior(f)}</td>}
                  <td className={td}>{meta(f)}</td>
                  {!compacta && <td className={cx(td, der)}>{vs(f)}</td>}
                  <td className={td}>{sinMeta(f)}</td>
                  {!compacta && conMarcas && <td className={cx(td, 'px-2')} />}
                  <td className={cx(td, der)} />
                </tr>
              );
            return (
              <tr
                key={f.key}
                data-pendiente={s === 'pendiente' || undefined}
                className={cx(
                  'border-t border-metas-borde-suave',
                  s === 'error' ? 'bg-metas-error-fondo' : s === 'pendiente' && !soloLectura ? 'bg-metas-fila-pendiente' : '',
                )}
              >
                <td className={cx(td, 'text-center')}>
                  <div className="flex justify-center">
                    <Icono situacion={s} />
                  </div>
                </td>
                <td className={td}>{identidad(f)}</td>
                {!compacta && (
                  <td className={td}>
                    <span className="block">{vacioSi(f.puesto)}</span>
                    <span className="block text-xs text-metas-texto-2" title="Fecha de alta">
                      {fechaCorta(f.fechaAlta)}
                    </span>
                  </td>
                )}
                {!compacta && (
                  <td className={cx(td, der, 'num')} title={tituloReferencia}>
                    {pesosEnteros(ventaReferencia(f))}
                  </td>
                )}
                {!compacta && <td className={cx(td, der, 'num text-metas-texto-2')}>{semanaAnterior(f)}</td>}
                <td className={td}>
                  {meta(f)}
                  {ajuste(f)}
                  {compacta && vsPromedio(f) !== null && (
                    <div className="mt-1 text-right text-xs">
                      {vs(f)} <span className="text-metas-texto-2">vs. prom.</span>
                    </div>
                  )}
                </td>
                {!compacta && <td className={cx(td, der)}>{vs(f)}</td>}
                <td className={td}>{sinMeta(f)}</td>
                {!compacta && conMarcas && <td className={cx(td, 'px-2')}>{marcas(f, 'columna')}</td>}
                <td className={cx(td, der)}>
                  <div className="flex items-center justify-end gap-0.5">
                    {!compacta && botonApoyo(f)}
                    {menu(f, compacta)}
                  </div>
                </td>
              </tr>
            );
          })}
          {!filas.length && (
            <tr>
              <td colSpan={compacta ? 5 : conMarcas ? 10 : 9} className={cx(td, 'text-metas-texto-2')}>
                Sin colaboradores en la plantilla
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {/* Fuera de la tabla: dentro de la cabecera se leería dos veces. */}
      {!compacta && conMarcas && (
        <span id={idTituloMarcas} className="sr-only">
          {TITULO_MARCAS}
        </span>
      )}
      {dialogos}
    </>
  );
};

export default ColaboradoresTabla;
